import {localStorageManager} from 'common/contexts/storage-manager';
import {isTestEnv} from 'common/report/util/env';
import {getLocation} from 'common/util/url';

import {gracefulJsonParse} from './util';

// `window.pylon` (lowercase) is the config object; `window.Pylon` (uppercase)
// is the programmatic API. Identity is set by mutating `window.pylon.chat_settings`.
// Docs: https://docs.usepylon.com/pylon-docs/chat-widget
type PylonProgrammaticApi = ((action: string, ...args: unknown[]) => unknown) & {q?: IArguments[]};

interface PylonChatSettings {
  app_id: string;
  email?: string;
  name?: string;
  email_hash?: string;
}

declare global {
  interface Window {
    Pylon?: PylonProgrammaticApi;
    pylon?: {chat_settings: PylonChatSettings};
  }
}

const PYLON_STORAGE_KEY = `pylon-store`;

// Must match the custom field slug configured in the Pylon workspace(s).
const PYLON_REPLAY_URL_FIELD = `session_replay_link`;

enum PylonWidgetTrackingEvent {
  LiveChatStarted = `Live Chat Started`,
}

interface PylonStoreShape {
  widgetShown?: boolean;
}

function persistChatState(state: PylonStoreShape) {
  localStorageManager.setItem(PYLON_STORAGE_KEY, JSON.stringify(state));
}

export function hidePylonWidget() {
  window.Pylon?.(`hide`);
}

// Kick off the script download without identifying or showing the widget.
// Safe to call repeatedly: `loadPylonWidget` dedupes via `_pylonReadyPromises`.
export function prewarmPylonWidget(appId: string): void {
  if (!appId) {
    return;
  }
  void loadPylonWidget(appId);
}

export function activatePersistedPylonChat(
  userName: string,
  userEmail: string,
  appId: string,
  emailHash: string,
): boolean {
  const store = gracefulJsonParse<PylonStoreShape>(localStorageManager.getItem(PYLON_STORAGE_KEY));
  if (store?.widgetShown) {
    activatePylonChat(userName, userEmail, appId, emailHash);
    return true;
  }
  return false;
}

// Fired only on user-initiated opens (the explicit Help → Live Chat click).
// Persistence-resume on page reload deliberately doesn't fire this so the
// event count reflects real chat starts, not page reloads.
export function trackPylonLiveChatStarted() {
  mixpanel.track(`[Help Widget] ${PylonWidgetTrackingEvent.LiveChatStarted}`, {
    $current_url: getLocation().href,
  });
}

// Attach the in-progress session replay URL so support can watch what the
// user was doing when they reached out. The field is ignored by Pylon if the
// workspace lacks the matching custom field slug, so this degrades safely.
export function setPylonReplayUrlField() {
  // get_session_replay_url returns "" when no recording is active.
  const replayUrl = mixpanel.get_session_replay_url?.();
  if (!replayUrl) {
    return;
  }
  window.Pylon?.(`setNewIssueCustomFields`, {[PYLON_REPLAY_URL_FIELD]: replayUrl});
}

// Pylon's API does not dedupe handlers, so register once per page.
let _listenersRegistered = false;

export async function activatePylonChat(userName: string, userEmail: string, appId: string, emailHash: string) {
  // An empty appId or emailHash means the backend couldn't resolve this
  // tier's app_id or hash secret yet - skip the bootstrap so the page
  // degrades to "no chat" instead of loading an unverified widget.
  if (!appId || !emailHash) {
    return;
  }
  const ready = await loadPylonWidget(appId);
  if (!ready) {
    return;
  }

  if (window.pylon) {
    window.pylon.chat_settings.email = userEmail;
    window.pylon.chat_settings.name = userName;
    window.pylon.chat_settings.email_hash = emailHash;
  }

  if (!_listenersRegistered) {
    window.Pylon?.(`onShow`, () => {
      persistChatState({widgetShown: true});
    });
    window.Pylon?.(`onHide`, () => {
      persistChatState({widgetShown: false});
    });
    _listenersRegistered = true;
  }

  setPylonReplayUrlField();

  window.Pylon?.(`show`);
  persistChatState({widgetShown: true});
}

function installPylonBootstrap(appId: string): HTMLScriptElement | null {
  const scriptSrc = `https://widget.usepylon.com/widget/${appId}`;
  if (!window.Pylon) {
    const queue: IArguments[] = [];
    const stub = function () {
      // eslint-disable-next-line prefer-rest-params
      queue.push(arguments);
    } as PylonProgrammaticApi;
    stub.q = queue;
    window.Pylon = stub;
  }
  window.pylon = window.pylon ?? {chat_settings: {app_id: appId}};

  const existing = document.head.querySelector<HTMLScriptElement>(`script[src='${scriptSrc}']`);
  if (existing) {
    return existing;
  }
  const script = document.createElement(`script`);
  script.src = scriptSrc;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
  return script;
}

// Keyed by appId so a tier change between activations doesn't return the
// stale promise.
const _pylonReadyPromises = new Map<string, Promise<boolean>>();
function loadPylonWidget(appId: string): Promise<boolean> {
  if (isTestEnv() || !appId) {
    return Promise.resolve(false);
  }
  const cached = _pylonReadyPromises.get(appId);
  if (cached) {
    return cached;
  }
  const promise = new Promise<boolean>((resolve) => {
    const script = installPylonBootstrap(appId);
    if (!script) {
      resolve(false);
      return;
    }
    let settled = false;
    const settle = (ok: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      if (!ok) {
        _pylonReadyPromises.delete(appId);
      }
      resolve(ok);
    };
    if (script.dataset.pylonReady === `true`) {
      settle(true);
      return;
    }
    script.addEventListener(`load`, () => {
      script.dataset.pylonReady = `true`;
      settle(true);
    });
    script.addEventListener(`error`, () => settle(false));
    window.setTimeout(() => settle(false), 10_000);
  });
  _pylonReadyPromises.set(appId, promise);
  return promise;
}
